import Axios, { CancelToken } from "axios";
import {
  EMensagemDestinatario,
  EUserType,
  IFiltroMateria,
  IMensagem,
  IMensagemArquivo,
  IMensagemAtribuicao,
  IMensagemLink,
  IMensagemPerguntaFormatada,
  IFiltroSerie,
  IFiltroTurma,
  IUser,
  IFiltroCurso,
  IMensagemCategoria as IMMMensagemCategoria,
} from "@deltasge/marauders-map";

import { api } from "configs";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import { sendS3File } from "utils/process-files-s3";
import { format, set } from "date-fns";
import { IFilterMensagem } from "interfaces";
import { WithRequired } from "@deltasge/marauders-map/lib/utils";

export interface IPreMensagemArquivo extends IMensagemArquivo {
  file?: File;
}

export type IMensagemCategoria = WithRequired<IMMMensagemCategoria, "id">;

const getSubtitle = ({
  curso,
  serie,
  turma,
  materia,
}: {
  curso?: IFiltroCurso;
  serie?: IFiltroSerie;
  turma?: IFiltroTurma;
  materia?: IFiltroMateria;
}): string => {
  const title = "Publicações";
  if (turma)
    return title
      .concat(` da turma ${turma.tituloTurma}`)
      .concat(` - ${materia?.nome ?? "Todas as matérias"}`);
  if (serie)
    return title
      .concat(` da série ${serie.descricao}`)
      .concat(` - ${materia?.nome ?? "Todas as matérias"}`);
  if (curso)
    return title
      .concat(` do curso ${curso.nome}`)
      .concat(` - ${materia?.nome ?? "Todas as matérias"}`);
  return title
    .concat(" de todos os cursos")
    .concat(` - ${materia?.nome ?? "Todas as matérias"}`);
};

const getDestinatario = ({
  atribuicoes,
}: {
  atribuicoes?: IMensagemAtribuicao[];
}): EMensagemDestinatario[] => {
  if (!atribuicoes || !atribuicoes[0]) return [];

  const result: EMensagemDestinatario[] = [];

  if (atribuicoes[0].aluno) result.push(EMensagemDestinatario.Alunos);
  if (atribuicoes[0].responsavel)
    result.push(EMensagemDestinatario.Responsáveis);
  if (atribuicoes[0].loginExtra)
    result.push(EMensagemDestinatario["Logins extra do Aluno"]);
  if (atribuicoes[0].professor) result.push(EMensagemDestinatario.Professores);

  return result;
};

const getMensagem = async ({
  idMensagem,
  cancelToken,
  para = "cadastro",
}: {
  idMensagem: number;
  cancelToken: CancelToken;
  para?: "cadastro" | "detalhes";
}): Promise<IMensagem | undefined> => {
  try {
    let select = [
      "mensagemCategoria.id",
      "mensagemCategoria.tipo",
      "mensagemCategoria.nome",
      "mensagemCategoria.aluno",
      "mensagemAtribuicoes.aluno",
      "mensagemAtribuicoes.loginExtra",
      "mensagemAtribuicoes.professor",
      "mensagemAtribuicoes.responsavel",
      "mensagemAtribuicoes.idCurso",
      "mensagemAtribuicoes.curso",
      "mensagemAtribuicoes.curso.id",
      "mensagemAtribuicoes.curso.nome",
      "mensagemAtribuicoes.idSerie",
      "mensagemAtribuicoes.serie",
      "mensagemAtribuicoes.serie.id",
      "mensagemAtribuicoes.serie.descricao",
      "mensagemAtribuicoes.idTurma",
      "mensagemAtribuicoes.turma",
      "mensagemAtribuicoes.turma.id",
      "mensagemAtribuicoes.turma.tituloTurma",
      "mensagemAtribuicoes.idMateria",
      "mensagemAtribuicoes.materia",
      "mensagemAtribuicoes.materia.id",
      "mensagemAtribuicoes.materia.nome",
      "mensagemAtribuicoes.id",
      "mensagemAtribuicoes.idMensagem",
      "mensagemAtribuicoes.anoLetivo",
      "mensagemAtribuicoes.mensagemAtribuicaoResponsaveis",
      "mensagemAtribuicoes.mensagemAtribuicaoResponsaveis.id",
      "mensagemAtribuicoes.mensagemAtribuicaoResponsaveis.responsavel",
      "mensagemAtribuicoes.mensagemAtribuicaoResponsaveis.responsavel.nome",
      "mensagemAtribuicoes.mensagemAtribuicaoResponsaveis.responsavel.codigo",
      "mensagemAtribuicoes.mensagemAtribuicaoLoginsExtras",
      "mensagemAtribuicoes.mensagemAtribuicaoLoginsExtras.id",
      "mensagemAtribuicoes.mensagemAtribuicaoLoginsExtras.loginExtra",
      "mensagemAtribuicoes.mensagemAtribuicaoLoginsExtras.loginExtra.nome",
      "mensagemAtribuicoes.mensagemAtribuicaoLoginsExtras.loginExtra.tipo",
      "mensagemAtribuicoes.mensagemAtribuicaoLoginsExtras.loginExtra.codigo",
      "mensagemAtribuicoes.mensagemAtribuicaoProfessores",
      "mensagemAtribuicoes.mensagemAtribuicaoProfessores.id",
      "mensagemAtribuicoes.mensagemAtribuicaoProfessores.professor",
      "mensagemAtribuicoes.mensagemAtribuicaoProfessores.professor.nome",
      "mensagemAtribuicoes.mensagemAtribuicaoProfessores.professor.codigo",
      "mensagemAtribuicoes.mensagemAtribuicaoAlunos",
      "mensagemAtribuicoes.mensagemAtribuicaoAlunos.id",
      "mensagemAtribuicoes.mensagemAtribuicaoAlunos.aluno",
      "mensagemAtribuicoes.mensagemAtribuicaoAlunos.aluno.nome",
      "mensagemAtribuicoes.mensagemAtribuicaoAlunos.aluno.nomeusual",
      "mensagemAtribuicoes.mensagemAtribuicaoAlunos.aluno.codigo",
      "mensagemArquivos.arquivo.caminhoArquivo",
      "mensagemArquivos.arquivo.hash",
      "mensagemArquivos.arquivo.nomeArquivo",
      "mensagemArquivos.idArquivo",
      "mensagemArquivos.idMensagem",
      "mensagemArquivos.ordem",
      "mensagemLinks.id",
      "mensagemLinks.idMensagem",
      "mensagemLinks.ordem",
      "mensagemLinks.url",
      "perguntasFormatadas.id",
      "perguntasFormatadas.idMensagem",
      "perguntasFormatadas.nome",
    ];

    if (para == "detalhes") {
      select = [
        "id",
        "createdAt",
        "updatedAt",
        "enviarEm",
        "permiteResposta",
        "respostaComAnexo",
        "programarEnvio",
        "respostaAte",
        "notificaResposta",
        "texto",
        "titulo",
        "rascunho",
        "idMensagemCategoria",
        "usuario.nome",
        "professor.nome",
        "mensagemAtribuicoes.aluno",
        "mensagemAtribuicoes.loginExtra",
        "mensagemAtribuicoes.professor",
        "mensagemAtribuicoes.responsavel",
        "mensagemAtribuicoes.curso.nome",
        "mensagemAtribuicoes.serie.descricao",
        "mensagemAtribuicoes.turma.tituloTurma",
        "mensagemAtribuicoes.materia.nome",
        "mensagemAtribuicoes.id",
        "mensagemArquivos.arquivo.caminhoArquivo",
        "mensagemArquivos.arquivo.hash",
        "mensagemArquivos.arquivo.nomeArquivo",
        "mensagemArquivos.ordem",
        "mensagemArquivos.idArquivo",
        "mensagemArquivos.idMensagem",
        "mensagemLinks.ordem",
        "mensagemLinks.url",
        "mensagemLinks.id",
        "perguntasFormatadas.nome",
        "perguntasFormatadas.id",
        "mensagemCategoria.id",
        "mensagemCategoria.tipo",
        "mensagemCategoria.nome",
        "mensagemCurtidas.id",
      ];
    }

    const { data } = await api.get<IMensagem>(
      `educador/mensagem/${idMensagem}`,
      {
        cancelToken: cancelToken,
        params: {
          select,
          order: {
            mensagemArquivos: { ordem: "ASC" },
          },
        },
      }
    );
    return data;
  } catch (err) {
    if (!Axios.isCancel(err)) {
      snack.warning(getError(err));
    }
  }
};

const handlePerguntaFormatada = ({
  pergunta,
  perguntasFormatadas,
  setValue,
  hideDialog,
  hasDelete = false,
  oldPergunta,
}: {
  pergunta: IMensagemPerguntaFormatada;
  perguntasFormatadas?: IMensagemPerguntaFormatada[];
  setValue: (key: keyof IMensagem, value: unknown) => void;
  hideDialog?: () => void;
  hasDelete?: boolean;
  oldPergunta?: IMensagemPerguntaFormatada;
}): void => {
  if (hasDelete) {
    setValue(
      "perguntasFormatadas",
      perguntasFormatadas?.map((m) => ({
        ...m,
        deletedAt: m.nome == pergunta.nome ? new Date() : m.deletedAt,
      }))
    );
  } else {
    if (!perguntasFormatadas) {
      setValue("perguntasFormatadas", [pergunta]);
    } else {
      const index = perguntasFormatadas.findIndex(
        (f) => f.nome == (oldPergunta ?? pergunta).nome
      );
      if (index >= 0) {
        perguntasFormatadas[index] = pergunta;
      } else {
        perguntasFormatadas.push(pergunta);
      }
      setValue("perguntasFormatadas", perguntasFormatadas);
    }
    if (hideDialog) hideDialog();
  }
};

const handleLink = ({
  link,
  links,
  setValue,
  hideDialog,
  hasDelete = false,
  oldLink,
}: {
  link: IMensagemLink;
  links?: IMensagemLink[];
  setValue: (key: keyof IMensagem, value: unknown) => void;
  hideDialog?: () => void;
  hasDelete?: boolean;
  oldLink?: IMensagemLink;
}): void => {
  if (hasDelete) {
    setValue(
      "mensagemLinks",
      links?.map((m) => ({
        ...m,
        deletedAt: m.url == link.url ? new Date() : m.deletedAt,
      }))
    );
  } else {
    if (!links) {
      setValue("mensagemLinks", [link]);
    } else {
      const index = links.findIndex((f) => f.url == (oldLink ?? link).url);
      if (index >= 0) {
        links[index] = link;
      } else {
        links.push(link);
      }
      setValue("mensagemLinks", links);
    }
    if (hideDialog) hideDialog();
  }
};

const handleSubmitMensagem = async ({
  payload,
  cancelToken,
  handleClose,
  usuario,
}: {
  payload: IMensagem;
  cancelToken: CancelToken;
  handleClose: () => void;
  usuario: IUser;
}) => {
  try {
    const mensagemArquivos = await Promise.all(
      (payload.mensagemArquivos ?? []).map(
        async (item): Promise<IMensagemArquivo> => {
          const { file } = item as IPreMensagemArquivo;
          if (file) {
            const arquivo = await sendS3File({
              file,
            });
            item.idArquivo = arquivo.id ?? "";
            item.idMensagem = payload.id ?? null;
          }
          return item;
        }
      )
    );
    payload.mensagemArquivos = mensagemArquivos;

    payload.idUsuario = null;
    payload.idProfessor = null;
    if (usuario.tipoUsuario == EUserType.Professor) {
      payload.idProfessor = usuario.id ?? null;
    } else {
      payload.idUsuario = usuario.id ?? null;
    }

    if (payload.id) {
      await api.put(`educador/mensagem/${payload.id}`, payload, {
        cancelToken,
        params: {
          sendNotification: 1,
        },
      });
    } else {
      await api.post("educador/mensagem", payload, {
        cancelToken,
        params: {
          sendNotification: 1,
        },
      });
    }
    handleClose();
  } catch (err) {
    if (!Axios.isCancel(err)) {
      snack.warning(getError(err));
    }
  }
};

const showTabDetalhesMensagem = ({
  atr,
  tipo,
}: {
  atr?: IMensagemAtribuicao[];
  tipo: "aluno" | "responsavel" | "professor" | "loginExtra";
}): boolean => {
  if (!atr) return false;
  return (
    (tipo == "aluno" && atr.some((f) => f.aluno)) ||
    (tipo == "responsavel" && atr.some((f) => f.responsavel)) ||
    (tipo == "loginExtra" && atr.some((f) => f.loginExtra)) ||
    (tipo == "professor" && atr.some((f) => f.professor))
  );
};

const getWhereMensagensRecebidas = ({
  idCategoria,
  filter,
}: {
  filter: IFilterMensagem;
  idCategoria?: string;
}) => {
  let dataIni: string | undefined = undefined;
  let dataFim: string | undefined = undefined;
  if (filter.dataIni && filter.dataFim) {
    dataIni = format(filter.dataIni, "yyyy-MM-dd HH:mm:ss");
    dataFim = format(
      set(filter.dataFim, { hours: 23, minutes: 59 }),
      "yyyy-MM-dd HH:mm:ss"
    );
  }
  let turma:
    | {
        idCurso?: number;
        idSerie?: number;
        idTurma?: number;
      }
    | undefined;
  if (filter.idCurso || filter.idSerie || filter.idTurma) {
    turma = {
      idCurso: filter.idCurso,
      idSerie: filter.idSerie,
      idTurma: filter.idTurma,
    };
  }
  return {
    idCategoria,
    ["texto.like"]: filter.texto,
    dataIni,
    dataFim,
    turma,
  };
};

const camposPublicacao = (raiz?: string) =>
  [
    "copiaEmail",
    "enviarEm",
    "excluido",
    "exportado",
    "id",
    "idMensagemCategoria",
    "idProfessor",
    "idUsuario",
    "permiteResposta",
    "notificaResposta",
    "programarEnvio",
    "respostaAte",
    "respostaComAnexo",
    "texto",
    "mensagemAtribuicoes",
    "mensagemLinks",
    "mensagemArquivos.ordem",
    "mensagemArquivos.idArquivo",
    "mensagemArquivos.idMensagem",
    "mensagemArquivos.arquivo.caminhoArquivo",
    "mensagemArquivos.arquivo.hash",
    "mensagemArquivos.arquivo.nomeArquivo",
    "perguntasFormatadas",
    "perguntasFormatadas.id",
    "perguntasFormatadas.nome",
    "mensagemCategoria.id",
    "mensagemCategoria.tipo",
    "mensagemCategoria.nome",
  ].map((campo) => `${raiz ? raiz + "." : ""}${campo}`);

export const utilMensagem = {
  getDestinatario,
  getMensagem,
  getSubtitle,
  handlePerguntaFormatada,
  handleLink,
  handleSubmitMensagem,
  showTabDetalhesMensagem,
  getWhereMensagensRecebidas,
  camposPublicacao,
};
